import { supabase } from './supabase'

// ═══════════════════════════════════════════════════════════════════════════
// API base
// ═══════════════════════════════════════════════════════════════════════════
//
// All backend calls go through this prefix. Default `/api`, overridable via the
// `VITE_API_URL` build-time env var.
//
// In dev, vite.config.ts proxies `/api` → http://localhost:8080 and STRIPS the
// `/api` prefix, so `${API_BASE}/health` reaches the FastAPI `/health` route and
// `${API_BASE}/events/<name>/stream` reaches `/events/<name>/stream`. Keep the
// default in sync with the proxy's rewrite rule.

export const API_BASE = import.meta.env.VITE_API_URL || '/api'

// ═══════════════════════════════════════════════════════════════════════════
// Auth helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Current Supabase access token, or null when signed out. */
async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Auth headers for authenticated requests. Returns an empty object when there's
 * no session so callers can spread it unconditionally.
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Bounce to the login page, preserving where the user was headed. */
function redirectToLogin(): void {
  window.location.href =
    '/login?returnTo=' + encodeURIComponent(window.location.pathname + window.location.search)
}

// ═══════════════════════════════════════════════════════════════════════════
// apiFetch — JSON request helper with auth + 401 handling
// ═══════════════════════════════════════════════════════════════════════════

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Authenticated JSON fetch. Attaches the Supabase bearer token, sets the JSON
 * content-type, and parses the response body as `T`. On 401 it redirects to
 * /login and throws. Use for plain request/response endpoints; use
 * `streamWorkflow` for SSE workflow streams.
 *
 * @param endpoint path relative to `API_BASE`, e.g. `/health` or `/foo/bar`
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  })

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin()
    }
    const body = await response.text()
    throw new ApiError(response.status, body || `HTTP ${response.status}`)
  }

  // 204 / empty bodies → undefined; otherwise parse JSON.
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

// ═══════════════════════════════════════════════════════════════════════════
// streamWorkflow — generic SSE client for workflow streams
// ═══════════════════════════════════════════════════════════════════════════
//
// Generalized from the project's per-workflow `streamX` functions. Every
// workflow SSE endpoint follows the same wire shape:
//
//   POST ${API_BASE}<path>            (path e.g. `/events/<name>/stream`)
//   Authorization: Bearer <token>
//   Content-Type: application/json
//   body: <whatever the workflow's input schema expects>
//
//   ← text/event-stream of frames separated by a blank line, each line:
//       data: {"type": "...", ...}\n\n
//     A literal `data: [DONE]` (or a `{"type":"...complete"}` event) ends it.
//
// This helper handles the transport (auth, POST, ReadableStream decode, frame
// splitting, JSON parse, 401 redirect) and dispatches every parsed event to a
// single `onEvent(event)` callback. The caller switches on `event.type` for its
// workflow's specific event names — that keeps the baseline workflow-agnostic.

/** A parsed SSE event. `type` is the discriminator; other fields are payload. */
export interface WorkflowEvent {
  type?: string
  [key: string]: unknown
}

export interface StreamWorkflowCallbacks {
  /** Fired once per parsed `data:` event. Switch on `event.type`. */
  onEvent: (event: WorkflowEvent) => void
  /** Fired on network failure, non-OK HTTP status, or a `session_error`/`error` event. */
  onError: (message: string) => void
  /** Fired exactly once when the stream closes cleanly (reader done / `[DONE]`). */
  onDone: () => void
}

/**
 * Stream a workflow's SSE endpoint.
 *
 * @param path     path relative to `API_BASE`, e.g. `/events/<name>/stream`
 * @param body     request body — serialized to JSON; pass the workflow's input shape
 * @param callbacks { onEvent, onError, onDone }
 *
 * @example
 *   await streamWorkflow('/events/myflow/stream', { content, session_id }, {
 *     onEvent: (ev) => {
 *       if (ev.type === 'content_delta') append(String(ev.delta ?? ''))
 *       else if (ev.type === 'session_complete') finalize(ev)
 *     },
 *     onError: (msg) => setError(msg),
 *     onDone: () => setStreaming(false),
 *   })
 */
export async function streamWorkflow(
  path: string,
  body: unknown,
  callbacks: StreamWorkflowCallbacks
): Promise<void> {
  const authHeaders = await getAuthHeaders()

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(body),
    })
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'Network error')
    return
  }

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin()
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line. The trailing fragment may be
      // a partial frame; keep it for the next read.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() || ''

      for (const frame of frames) {
        // A frame may span multiple lines; only `data:` lines carry payload.
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6)
          if (raw === '[DONE]') {
            callbacks.onDone()
            return
          }
          let event: WorkflowEvent
          try {
            event = JSON.parse(raw)
          } catch {
            continue
          }
          // Surface error-shaped events through onError; everything else flows
          // to onEvent for the caller to route by `event.type`.
          if (event.type === 'error' || event.type === 'session_error') {
            callbacks.onError(String(event.error ?? 'Unknown error'))
          } else {
            callbacks.onEvent(event)
          }
        }
      }
    }
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'Stream read error')
    return
  } finally {
    reader.releaseLock()
  }

  callbacks.onDone()
}

// ═══════════════════════════════════════════════════════════════════════════
// streamSteward — SSE client for the steward workflow
// ═══════════════════════════════════════════════════════════════════════════
//
// Steward is a self-governing Johnny.Decimal filing assistant. A turn streams,
// in order (the DAG is linear, so events arrive strictly node-by-node):
//
//   <node>_start  {}                                    ← node began real work
//     thinking_start / thinking {thinking} / thinking_complete   (deep mode)
//     tool_start  {tool, label}                          ← a tool was invoked
//     tool_done   {tool, count}                          ← …and returned
//   <node>_done   {output:{...}} | {skipped|empty:true}  ← node finished
//   …repeated per node (route → place → compose → commission → retrieve → converse)…
//   session_complete {session_id, response}              ← FINAL assistant markdown
//
// A node that isn't on the routed branch emits ONLY `<node>_done {skipped:true}`
// (no `_start`), so the frontend never shows a step for it. Nodes that run emit
// `_start` first — that's what drives the live "running" indicator. The thinking
// + tool events between a `_start` and its `_done` belong to that node.
//
// There is NO incremental assistant-text stream in v1: the per-node steps are
// surfaced as a live folding timeline while the turn runs, then
// `session_complete.response` is rendered as the assistant message.

/** Which DAG node a `*_done` event came from. */
export type StewardStageType = 'ingest' | 'route' | 'place' | 'compose' | 'retrieve' | 'commission' | 'commonplace_keeper'

/** The specialist (surgeon) mode signal — emitted by the entry gate when a `/commonplace_keeper …`
 *  summon opens specialist mode, or `/exit` (or the ✕) closes it back to the generalist. */
export interface StewardSpecialistEvent {
  active: boolean
  exited: boolean
  harness: string
  task: string
}

/** Parsed payload of a steward `*_done` event. Either a node `output`, or a
 *  control flag (`skipped` / `empty`) when the node didn't run / had nothing. */
export interface StewardStageOutput {
  output?: Record<string, unknown>
  skipped?: boolean
  empty?: boolean
}

/** A tool invocation surfaced inside a node's step (e.g. `semantic_search`). */
export interface StewardToolEvent {
  tool: string
  /** Human label shown while running (from `tool_start`). */
  label?: string
  /** Result size when it returns (from `tool_done`). */
  count?: number
  status: 'running' | 'done'
}

/** One file dropped with a turn — IngestNode reads + files it. Bytes are base64 (no
 *  `data:<mime>;base64,` prefix). */
export interface StewardAttachment {
  filename: string
  mime: string
  data_base64: string
}

export interface StreamStewardParams {
  session_id?: string | null
  content: string
  run_mode?: 'fast' | 'deep'
  /** Folder path of the Job card on screen right now, so a bare "delete this" / "open it"
   *  resolves to it server-side instead of querying the whole map. null when none on screen. */
  active_job_path?: string | null
  /** Files dropped with this turn. IngestNode reads + files them before Retrieve speaks. */
  attachments?: StewardAttachment[]
}

export interface StreamStewardCallbacks {
  /** A DAG node began real work (`<node>_start`) — drives the live "running"
   *  indicator. Only fired for nodes that actually run (skipped nodes don't). */
  onStageStart?: (stage: StewardStageType) => void
  /** Deep mode: a thinking stream is starting. */
  onThinkingStart?: () => void
  /** Deep mode: an extended-thinking delta. Belongs to the currently-running node. */
  onThinkingDelta?: (delta: string) => void
  /** Deep mode: the thinking stream finished (no payload in v1). */
  onThinkingComplete?: () => void
  /** A tool was invoked (`tool_start`) or returned (`tool_done`) inside the
   *  currently-running node. */
  onTool?: (tool: StewardToolEvent) => void
  /** A DAG node finished. `stage` says which; `output` carries its result or a
   *  skipped/empty flag. */
  onStage?: (stage: StewardStageType, output: StewardStageOutput) => void
  /** Specialist (surgeon) mode opened (`active`) or closed (`exited`), from the entry gate. */
  onSpecialist?: (ev: StewardSpecialistEvent) => void
  /** The turn finished. `response` is the FINAL assistant markdown; on the
   *  first turn `session_id` carries the freshly-minted session id. */
  onComplete: (result: { session_id?: string; response: string }) => void
  onError: (message: string) => void
}

const STEWARD_STAGES: StewardStageType[] = [
  'ingest', 'route', 'place', 'compose', 'retrieve', 'commission', 'commonplace_keeper',
]

/** Map a `<node>_<suffix>` event name to (stage, suffix), or null if neither. */
function stewardStageEvent(
  eventType: string | undefined
): { stage: StewardStageType; suffix: 'start' | 'done' } | null {
  if (!eventType) return null
  for (const suffix of ['start', 'done'] as const) {
    if (eventType.endsWith(`_${suffix}`)) {
      const stage = eventType.slice(0, -(suffix.length + 1)) as StewardStageType
      if (STEWARD_STAGES.includes(stage)) return { stage, suffix }
    }
  }
  return null
}

export async function streamSteward(
  params: StreamStewardParams,
  callbacks: StreamStewardCallbacks
): Promise<void> {
  await streamWorkflow(
    '/events/steward/stream',
    {
      // NEVER send user_id — the backend derives it from the bearer token.
      content: params.content,
      session_id: params.session_id ?? null,
      run_mode: params.run_mode ?? 'fast',
      active_job_path: params.active_job_path ?? null,
      attachments: params.attachments ?? [],
    },
    {
      onEvent: (ev) => {
        switch (ev.type) {
          case 'thinking_start':
            callbacks.onThinkingStart?.()
            break
          case 'thinking':
            callbacks.onThinkingDelta?.(String(ev.thinking ?? ''))
            break
          case 'thinking_complete':
            callbacks.onThinkingComplete?.()
            break
          case 'tool_start':
            callbacks.onTool?.({
              tool: String(ev.tool ?? 'tool'),
              label: typeof ev.label === 'string' ? ev.label : undefined,
              status: 'running',
            })
            break
          case 'tool_done':
            callbacks.onTool?.({
              tool: String(ev.tool ?? 'tool'),
              count: typeof ev.count === 'number' ? ev.count : undefined,
              status: 'done',
            })
            break
          case 'specialist_active':
            callbacks.onSpecialist?.({
              active: ev.active === true,
              exited: ev.exited === true,
              harness: typeof ev.harness === 'string' ? ev.harness : '',
              task: typeof ev.task === 'string' ? ev.task : '',
            })
            break
          case 'session_complete':
            callbacks.onComplete({
              session_id:
                typeof ev.session_id === 'string' ? ev.session_id : undefined,
              response: typeof ev.response === 'string' ? ev.response : '',
            })
            break
          default: {
            // The `<node>_start` / `<node>_done` stage events.
            const parsed = stewardStageEvent(ev.type)
            if (parsed?.suffix === 'start') {
              callbacks.onStageStart?.(parsed.stage)
            } else if (parsed?.suffix === 'done') {
              const out =
                ev.output && typeof ev.output === 'object'
                  ? (ev.output as Record<string, unknown>)
                  : undefined
              // The backend nests the control flags inside `output`
              // ({output:{skipped:true}}); accept a top-level form too.
              callbacks.onStage?.(parsed.stage, {
                output: out,
                skipped: out?.skipped === true || ev.skipped === true,
                empty: out?.empty === true || ev.empty === true,
              })
            }
            // Any other event type is ignored — keeps the client forward-compatible.
          }
        }
      },
      // `error` / `session_error` events are already routed to onError by
      // streamWorkflow; this catches transport-level failures too.
      onError: callbacks.onError,
      onDone: () => {
        // No-op: session_complete carries the authoritative end-of-turn signal.
        // streamWorkflow also fires onDone on `[DONE]`; the store flips
        // streaming off in onComplete, so a trailing onDone is harmless.
      },
    }
  )
}
// ═══════════════════════════════════════════════════════════════════════════
// Steward sessions — list / rehydrate / rename+pin / delete
// ═══════════════════════════════════════════════════════════════════════════

export interface StewardSessionSummary {
  session_id: string
  name: string
  total_turns: number
  pinned: boolean
  updated_at: string | null
  created_at: string | null
}

export interface StewardTurn {
  role: 'user' | 'assistant'
  content: string
  mode?: string
}

export interface StewardSessionDetail {
  session_id: string
  name: string | null
  total_turns: number
  pinned: boolean
  turns: StewardTurn[]
}

/** One specialist harness in the `/` trigger tree, with its prescribed tasks. */
export interface StewardTriggerTask {
  id: string
  label: string
  description: string
  requires_text: boolean
}
export interface StewardTriggerHarness {
  id: string
  label: string
  description: string
  tasks: StewardTriggerTask[]
}
export interface StewardTriggerRegistry {
  exit_verb: string
  harnesses: StewardTriggerHarness[]
}

/** The specialist trigger registry — powers the `/` suggestion tree (type `/` → harnesses;
 *  pick one, `/` again → its tasks). One source of truth shared with the backend parser. */
export function getStewardTriggers(): Promise<StewardTriggerRegistry> {
  return apiFetch<StewardTriggerRegistry>('/steward/triggers')
}

/** Recent sessions, pinned-first then most-recent (empty sessions hidden). */
export function listStewardSessions(): Promise<StewardSessionSummary[]> {
  return apiFetch<StewardSessionSummary[]>('/steward/sessions')
}

/** One session + its full turn history, for rehydration on switch. */
export function getStewardSession(sessionId: string): Promise<StewardSessionDetail> {
  return apiFetch<StewardSessionDetail>(`/steward/sessions/${sessionId}`)
}

// ── Inbox (the agent→consumer channel) ──────────────────────────────────────
export interface StewardDecisionQuestion {
  question: string
  options: string[]
  multi_select: boolean
  allow_other: boolean
}

export interface StewardInboxItem {
  session_id: string
  kind: 'notice' | 'decision'
  name: string
  read_at: string | null
  resolved_at: string | null
  decision_spec: { questions: StewardDecisionQuestion[] } | null
  source_ref: string | null
  created_at: string | null
}

export interface StewardInbox {
  badge: number // unresolved decisions — the things that need you
  items: StewardInboxItem[]
}

/** The agent→consumer inbox: unresolved decisions + unread notices. */
export function getStewardInbox(): Promise<StewardInbox> {
  return apiFetch<StewardInbox>('/steward/inbox')
}

/** Mark an inbox item read — a notice auto-ages out of the inbox after this. */
export function markStewardInboxRead(sessionId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/steward/inbox/${sessionId}/read`, { method: 'POST' })
}

/** Rename and/or pin a session. */
export function patchStewardSession(
  sessionId: string,
  patch: { name?: string; pinned?: boolean }
): Promise<StewardSessionSummary> {
  return apiFetch<StewardSessionSummary>(`/steward/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

/** Delete a session and its turns. */
export function deleteStewardSession(sessionId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/steward/sessions/${sessionId}`, { method: 'DELETE' })
}

// ═══════════════════════════════════════════════════════════════════════════
// Steward job/file cards — open a Job (and its files) as cards in the chat
// ═══════════════════════════════════════════════════════════════════════════

export interface StewardJobFile {
  name: string
  ext: string
  rel: string
}

export interface StewardJobBlock {
  label?: string
  date?: string
  time?: string | null
  duration_minutes?: number | null
  all_day?: boolean
}

export interface StewardJobCard {
  path: string
  title: string
  frontmatter: Record<string, unknown>
  blocks: StewardJobBlock[]
  body: string
  files: StewardJobFile[]
}

/** The Job brief + its working links (files), for a job card. */
export function getStewardJobCard(path: string): Promise<StewardJobCard> {
  return apiFetch<StewardJobCard>(`/steward/job?path=${encodeURIComponent(path)}`)
}

/** Mark a Job done from the app (the Job card's "Done" button). Captures the current chat
 *  thread into the folder as notes, then fires the close-out (outcome → Inbox). Returns
 *  instantly; the wrap-up runs in the background. */
export function completeStewardJob(
  path: string,
  sessionId?: string | null
): Promise<{ ok: boolean; message: string }> {
  return apiFetch(`/steward/job/complete`, {
    method: 'POST',
    body: JSON.stringify({ path, session_id: sessionId ?? null }),
  })
}

/** A resolved `steward://` reference — a Job or a structure level (area/category/project),
 *  for the hover-peek and the inline NoteCard. `files` is populated for jobs only. */
export interface StewardNote {
  kind: 'job' | 'area' | 'category' | 'project'
  path: string
  title: string
  body: string
  files: StewardJobFile[]
}

/** Resolve a `steward://<kind>/<id>` reference. `id` is a folder path (job) or a JD code
 *  (structure). Owner-scoped + path-safe on the server. */
export function getStewardNote(kind: string, id: string): Promise<StewardNote> {
  return apiFetch<StewardNote>(
    `/steward/note?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`
  )
}

/** Fetch a file inside a Job folder WITH auth, as a Blob — `<img>`/`<iframe>` can't
 *  send the bearer token, so file cards fetch the blob and use an object URL. */
export async function fetchStewardFileBlob(path: string): Promise<Blob> {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/steward/file?path=${encodeURIComponent(path)}`, {
    headers: { ...authHeaders },
  })
  if (!res.ok) {
    if (res.status === 401) redirectToLogin()
    throw new Error(`HTTP ${res.status}`)
  }
  return res.blob()
}

// ═══════════════════════════════════════════════════════════════════════════
// Canvas drawings (tldraw documents; plain CRUD, no SSE)
// ═══════════════════════════════════════════════════════════════════════════

export interface CanvasLayer {
  id: string
  name: string
  visible: boolean
  locked: boolean
}

export interface CanvasDrawingSummary {
  drawing_id: string
  title: string
  /** Sidebar grouping label; null = top level. */
  folder: string | null
  created_at: string | null
  updated_at: string | null
}

export interface CanvasDrawingFull extends CanvasDrawingSummary {
  /** tldraw TLStoreSnapshot (the `document` half of getSnapshot); null for a new drawing. */
  document: Record<string, unknown> | null
  layers: CanvasLayer[]
}

/** List the user's drawings, newest-updated first. Summaries only — no documents. */
export function listCanvasDrawings(): Promise<CanvasDrawingSummary[]> {
  return apiFetch<CanvasDrawingSummary[]>('/canvas/drawings')
}

export function createCanvasDrawing(
  title: string,
  layers?: CanvasLayer[]
): Promise<CanvasDrawingFull> {
  return apiFetch<CanvasDrawingFull>('/canvas/drawings', {
    method: 'POST',
    body: JSON.stringify({ title, layers }),
  })
}

export function getCanvasDrawing(drawingId: string): Promise<CanvasDrawingFull> {
  return apiFetch<CanvasDrawingFull>(`/canvas/drawings/${drawingId}`)
}

/** Partial save — send only the fields that changed.
 *  `folder: ''` moves the drawing to the top level; omit to leave unchanged. */
export function updateCanvasDrawing(
  drawingId: string,
  patch: {
    title?: string
    document?: Record<string, unknown>
    layers?: CanvasLayer[]
    folder?: string
  }
): Promise<CanvasDrawingSummary> {
  return apiFetch<CanvasDrawingSummary>(`/canvas/drawings/${drawingId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export function deleteCanvasDrawing(drawingId: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/canvas/drawings/${drawingId}`, {
    method: 'DELETE',
  })
}

// >>> NEW_WORKFLOW_API — commissioning-workflows inserts typed clients here <<<

// ═══════════════════════════════════════════════════════════════════════════
// Health
// ═══════════════════════════════════════════════════════════════════════════

/** Liveness probe — GET ${API_BASE}/health (proxied to the API's /health). */
export async function fetchHealth(): Promise<{ status?: string } & Record<string, unknown>> {
  return apiFetch('/health')
}
